import React from 'react';
import { Link } from 'react-router-dom';

import { useThrottledToast } from '../../hooks/useThrottledToast';

import { ArrowLeft } from '../../components/icons/arrow-left';
import { Switch } from '../../components/common/switch';
import { Love } from '../../components/icons/reactions/love';
import { Joy } from '../../components/icons/reactions/joy';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectEmotions } from '../../store/psyTools.slice';
import { Sadness } from '../../components/icons/reactions/sadness';
import { Fear } from '../../components/icons/reactions/fear';
import { Anger } from '../../components/icons/reactions/anger';
import { selectUser, setUserData } from '../../store/auth.slice';
import { useChangeUserMutation } from '../../services/users.api';
import { useTariffToolCheck } from '../../hooks/useTariffToolCheck';

const EmotionDetector = () => {
  const tool = 'emotions_detection';
  const isActiveByTariff = useTariffToolCheck(tool);
  const dispatch = useAppDispatch();
  const userData = useAppSelector(selectUser);
  const [changeUser] = useChangeUserMutation();
  const showToast = useThrottledToast();
  const emotions = useAppSelector(selectEmotions);

  const getEmotionSmile = (val: string) => {
    switch (val) {
      case 'Любовь':
        return <Love needHover={false} />;
      case 'Радость':
        return <Joy needHover={false} />;
      case 'Гнев':
        return <Anger needHover={false} />;
      case 'Страх':
        return <Fear needHover={false} />;
      case 'Грусть':
        return <Sadness needHover={false} />;
      default:
        return null;
    }
  };

  const handleCheck = async () => {
    try {
      if (!isActiveByTariff) return;
      if (!userData) return;

      if (userData?.active_psy_tools?.includes(tool)) {
        const arr = userData?.active_psy_tools.filter((el) => el !== tool);

        await changeUser({
          id: userData.id,
          active_psy_tools: arr,
        });
        dispatch(setUserData({ ...userData, active_psy_tools: arr }));
      } else {
        await changeUser({
          id: userData.id,
          active_psy_tools: [...userData?.active_psy_tools, tool],
        });
        dispatch(
          setUserData({
            ...userData,
            active_psy_tools: [...userData?.active_psy_tools, tool],
          }),
        );
      }
      showToast('success');
    } catch (error) {
      showToast('error');
    }
  };

  return (
    <div className="pb-[80px]">
      <h2 className="flex items-center gap-[14px] font-comfortaa font-semibold text-xl text-blackBase mb-5">
        <Link to={'/tools'} className="group">
          <ArrowLeft className="fill-blackBase group-hover:fill-redBase" width={21} height={16} />
        </Link>
        <span>Детектор эмоций</span>
      </h2>
      <p className="text-sm text-greyBase mb-5">
        Психологический инструмент Детектор Эмоций ZiNGiZi опирается на фундаментальные исследования Кэрола Изарда,
        используя его теорию дифференциальных эмоций для структурирования эмоционального опыта пользователя.
      </p>

      <div className="flex items-center gap-5 mb-[40px]">
        <Switch
          checked={userData?.active_psy_tools?.includes(tool)}
          disabled={!isActiveByTariff}
          setChecked={() => handleCheck()}
          label={'Использовать Детектор эмоций в видео-сессиях'}
        />
      </div>

      <section className="mb-10">
        <h3 className="font-semibold text-blackBase mb-3">
          Как «Детектор Эмоций» помогает дистанцированию?
        </h3>
        <p className="text-sm text-greyBase mb-4">
          Дистанцирование (или работа в мета-позиции) — это способность клиента увидеть свою эмоцию со стороны,
          отделить ее от «Я». Инструмент делает это наглядно:
        </p>
        <ul className="flex flex-col gap-4 list-none pl-0">
          <li>
            <span className="font-medium text-blackBase">Объективация чувства:</span>
            <span className="text-sm text-greyBase ml-1">
              Эмоция перестает быть «туманом внутри» и превращается в конкретное слово на экране. Клиент может ткнуть в
              него пальцем (буквально или мысленно), сказав: «Вот оно». Это переводит переживание из разряда «я есть
              гнев» в разряд «я испытываю гнев».
            </span>
          </li>
          <li>
            <span className="font-medium text-blackBase">Создание паузы (префронтальная кора включается):</span>
            <span className="text-sm text-greyBase ml-1">
              Процесс поиска нужного слова, чтения списка, выбора — требует когнитивных усилий. Это автоматически
              активирует рациональные отделы мозга, снижая накал в миндалевидном теле (центре страха и ярости). Это и
              есть физиологический механизм дистанцирования.
            </span>
          </li>
          <li>
            <span className="font-medium text-blackBase">Расширение словаря эмоций:</span>
            <span className="text-sm text-greyBase ml-1">
              Часто клиенты говорят «мне плохо» или «все нормально». Инструмент предлагает нюансы (например, разницу
              между «досадой» и «негодованием»). Называя вещи своими именами, клиент уже не слит с переживанием, а
              управляет им.
            </span>
          </li>
        </ul>
      </section>

      <section className="mb-10">
        <h3 className="font-semibold text-blackBase mb-4">Применение в разных модальностях</h3>
        <ul className="flex flex-col gap-6 list-none pl-0">
          <li>
            <span className="font-medium text-blackBase">🧠 КПТ-терапевт (Когнитивно-поведенческая терапия)</span>
            <p className="text-sm text-greyBase mt-2">
              Как использует: На этапе психообразования и заполнения дневника мыслей (ситуация — мысль — эмоция —
              поведение). Задача: Научить клиента точно дифференцировать эмоции, чтобы понять, какая когнитивная ошибка
              (например, «чтение мыслей» или «катастрофизация») вызывает именно ЭТО чувство. Пример: Клиент говорит:
              «Меня все бесит». Психолог предлагает уточнить: «Посмотри на список. Там есть и "раздражение", и
              "ярость", и "негодование". Что ближе к твоему состоянию? Обычно за раздражением стоит мысль "это
              несправедливо", а за яростью — "меня хотят уничтожить". Какая мысль крутится у тебя?»
            </p>
          </li>
          <li>
            <span className="font-medium text-blackBase">🔵 Гештальт-терапевт</span>
            <p className="text-sm text-greyBase mt-2">
              Как использует: Для работы с осознаванием здесь-и-сейчас и с фигурами/фоном. Задача: Помочь клиенту
              присвоить эмоцию, заметить, где она живет в теле, и какую потребность скрывает. Пример: «Ты выбрал слово
              "обида". Попробуй произнести его вслух. Где в теле сейчас отзывается это слово? Если бы у этой обиды был
              голос, что бы она сказала? Кому она адресована?» Инструмент помогает вытащить чувство из фона и сделать
              его фигурой для диалога.
            </p>
          </li>
          <li>
            <span className="font-medium text-blackBase">👂 Экзистенциальный психолог</span>
            <p className="text-sm text-greyBase mt-2">
              Как использует: Для выхода к данностям существования (смерть, свобода, изоляция, бессмысленность). Задача:
              Увидеть за повседневной эмоцией глубинный экзистенциальный конфликт. Пример: Клиент выбирает «тревогу» или
              «оцепенение». Психолог может спросить: «В этом списке есть "страх" (конкретной вещи) и есть "тревога"
              (беспредметное волнение). Твоя тревога — о чем она? Может быть, она касается выбора, который тебя лишает
              каких-то других возможностей (вина/свобода)? Или ощущения, что ты в этом совсем один (изоляция)?»
            </p>
          </li>
          <li>
            <span className="font-medium text-blackBase">👐 Телесный терапевт</span>
            <p className="text-sm text-greyBase mt-2">
              Как использует: Как мостик от телесного ощущения к вербализации. Задача: Помочь клиенту «перевести»
              телесный симптом на язык чувств. Пример: «Ты говоришь, что в груди ком и плечи зажаты. Давай посмотрим на
              экран. Какое из этих слов резонирует с тем, что происходит в теле? Может, это "тоска" или "душевная боль"?
              А теперь закрой глаза и снова вернись в тело. Что изменилось в ощущении комка, когда ты назвал его этим
              словом?»
            </p>
          </li>
          <li>
            <span className="font-medium text-blackBase">🎨 Арт-терапевт</span>
            <p className="text-sm text-greyBase mt-2">
              Как использует: Как этап пре-вербализации или для фокусировки творчества. Задача: Выбрать фокус для
              творческого выражения. Пример: «Посмотри на список. Какие три эмоции громче всех звучат в тебе сегодня?
              Теперь у тебя есть краски/пластилин/бумага. Попробуй изобразить не сами эмоции, а то, что находится между
              ними. Например, между "гневом" и "стыдом" часто есть очень интересное напряжение».
            </p>
          </li>
          <li>
            <span className="font-medium text-blackBase">💎 Транзактный анализ (ТА)</span>
            <p className="text-sm text-greyBase mt-2">
              Как использует: Для определения эго-состояния (Родитель, Взрослый, Ребенок). Задача: Определить, из какого
              эго-состояния идет реакция, и «переключить» клиента во Взрослого. Пример: «Ты выбрал "обиду" и "ревность".
              Это очень похоже на чувства вашего Внутреннего Ребенка. А теперь посмотри на список глазами Взрослого.
              Какая эмоция была бы уместна для нейтрального наблюдателя в этой ситуации? Может, это "сожаление" или
              "досада"? Давай дадим слово вашему Взрослому».
            </p>
          </li>
        </ul>
      </section>

      <div className="flex flex-col gap-[60px]">
        {emotions?.psy_tool_settings &&
          emotions.psy_tool_settings.map((emotion) => (
            <div key={emotion.id} className="flex flex-col md:flex-row gap-[40px]">
              <div className="w-[120px] flex flex-col items-center justify-center self-center gap-[15px] bg-grey5 p-5 rounded-[10px]">
                {getEmotionSmile(emotion.name)}
                <p className="font-medium text-blackBase">{emotion.name}</p>
              </div>
              <div className="flex flex-wrap content-start gap-[20px]">
                {emotion.value.map((item) => (
                  <div
                    key={item}
                    className="w-max font-medium text-sm text-blackBase bg-goldBase p-[5px_15px] rounded-[5px]"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default EmotionDetector;
